import React, { useCallback, useEffect, useState } from "react";
import { Document } from "../../__generated__/graphql";
import { Alert } from "../Alert";
import VStack from "../../ui_atoms/VStack";
import Loading from "../Loading";
import TextField from "../TextField";
import { useI18n } from "../../hooks/i18n-hooks";
import Button from "../Button";
import Modal from "../Modal";
import StackHeader from "../StackHeader";
import DropdownManager from "../../contexts/DropdownManager";
import * as FileSystem from "expo-file-system";
import * as Sharing from "expo-sharing";
import { customAlphabet } from "nanoid";
import DocumentViewer from "./DocumentViewer";
import { UploadDocumentType } from "../../hooks/documents";
import { setStatusBarBackgroundColor, setStatusBarStyle } from "expo-status-bar";

const generateShortId = customAlphabet("0123456789", 5);

export type DocumentEditorProps = {
    onSave?: (document: Document) => void | Promise<void>;
    onClose: () => void;
    onRemove?: (document: Document) => void;
    visible: boolean;
    document?: UploadDocumentType;
    mimeType?: string;
    processing?: boolean;
    isDarkMode?: boolean;
    fixedName?: string;
    saveButtonTitle?: string;
};

const DocumentEditor: React.FC<DocumentEditorProps> = ({
    onSave,
    onClose,
    onRemove,
    visible,
    document,
    mimeType,
    processing,
    isDarkMode,
    fixedName,
    saveButtonTitle,
}) => {
    const i18n = useI18n();
    const [documentTitle, setDocumentTitle] = useState<string>("");

    React.useEffect(() => {
        if (document?.title) {
            setDocumentTitle(document.title);
        } else {
            setDocumentTitle("");
        }
    }, [document]);

    const shareDocument = useCallback(() => {
        const fn = async () => {
            if (document?.documentUrl) {
                const ext = (() => {
                    switch (mimeType) {
                        case "application/pdf":
                            return "pdf";
                        default:
                            return document.documentUrl?.split(".").pop() || "bin";
                    }
                })();
                const randomFileId = generateShortId();
                const localUri = `${FileSystem.cacheDirectory || "/"}${(
                    documentTitle || `file-${randomFileId}`
                ).replaceAll(" ", "_")}.${ext}`;
                const downloadRet = await FileSystem.downloadAsync(
                    document.documentUrl || "",
                    localUri,
                );
                await Sharing.shareAsync(downloadRet.uri);
            }
        };
        void fn();
    }, [document?.documentUrl, documentTitle, mimeType]);

    const doSaveDocument = React.useCallback(() => {
        if (documentTitle.length === 0) return;

        if (document) {
            void onSave?.({
                ...(document as Document),
                title: documentTitle,
            });
        }
    }, [document, documentTitle, onSave]);

    const doRemove = React.useCallback(() => {
        if (onRemove && document) {
            Alert.alert(
                i18n.t("components.document.remove_document_title"),
                i18n.t("components.document.remove_document_description"),
                [
                    {
                        text: i18n.t("components.document.remove_document_confirm"),
                        onPress: () => onRemove(document as Document),
                        style: "destructive",
                    },
                    {
                        text: i18n.t("components.document.remove_document_cancel"),
                        style: "cancel",
                    },
                ],
                { cancelable: true },
            );
        }
    }, [document, i18n, onRemove]);

    const doClose = React.useCallback(() => {
        onClose();
    }, [onClose]);

    const doShare = React.useCallback(() => {
        shareDocument();
    }, [shareDocument]);

    useEffect(() => {
        if (isDarkMode && visible) {
            setStatusBarBackgroundColor("black", true);
            setStatusBarStyle("light", true);

            return () => {
                setStatusBarBackgroundColor("white", true);
                setStatusBarStyle("dark", true);
            };
        }
    }, [isDarkMode, visible]);

    return (
        <Modal
            visible={visible}
            hideContainerBackground={true}
            bgColorName={isDarkMode ? "body" : undefined}
            fitContent
            animationType="slide"
        >
            <DropdownManager>
                <StackHeader
                    title={fixedName || documentTitle}
                    tintColor={isDarkMode ? "white" : undefined}
                    onLeftPress={doClose}
                    actions={[
                        {
                            label: i18n.t("components.document.share"),
                            icon: "share" as const,
                            onPress: doShare,
                        },
                        ...(onRemove
                            ? [
                                  {
                                      label: i18n.t("components.document.remove"),
                                      icon: "trash" as const,
                                      color: "red",
                                      onPress: doRemove,
                                  },
                              ]
                            : []),
                    ]}
                />
                <VStack
                    gap={1}
                    h={onSave ? "2/3" : undefined}
                    style={{
                        flex: 1,
                    }}
                >
                    {document?.documentUrl ? (
                        <DocumentViewer
                            documentUrl={document.documentUrl}
                            knownMimeType={mimeType}
                            tintColor={isDarkMode ? "white" : undefined}
                        />
                    ) : (
                        <Loading />
                    )}
                    {onSave && (
                        <VStack gap={1} v={1}>
                            {!fixedName && (
                                <TextField
                                    autoFocus
                                    style={{ alignSelf: "stretch" }}
                                    label={i18n.t("components.document.title_label")}
                                    placeholder={i18n.t("components.document.title_placeholder")}
                                    value={documentTitle}
                                    onChangeText={setDocumentTitle}
                                    onSubmitEditing={doSaveDocument}
                                    disabled={processing}
                                />
                            )}
                            <Button
                                testID="save-document"
                                size="large"
                                style={{ alignSelf: "stretch" }}
                                buttonMode="secondary"
                                label={saveButtonTitle || i18n.t("save")}
                                onPress={doSaveDocument}
                                processing={processing}
                                disabled={
                                    !fixedName &&
                                    (documentTitle.length === 0 || !document?.documentUrl)
                                }
                            />
                        </VStack>
                    )}
                </VStack>
            </DropdownManager>
        </Modal>
    );
};

export default DocumentEditor;
